import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";

// Components
import Text from "new_components/atoms/Text";
import AILogo from "new_components/molecules/AILogo";
import ChatTemplate from "new_components/templates/ChatTemplate";
import PromptModal from "./PromptModal";
import ChatHistory from "./components/ChatHistory";
import DataSourceCard from "new_components/molecules/DataSourceCard";
// import Button from "new_components/atoms/Button";
import { ICON_SET } from "new_components/atoms/Icon/IconSet";
import Icon from "new_components/atoms/Icon";
import Tooltip from "new_components/molecules/Tooltip";
import DescriptionInput from "new_components/molecules/DescriptionInput";
import ChatDialogBox from "./components/PromptDialog";
import ChatResponse from "./components/ChatResponse";
import DataSource from "new_components/organisms/DataSource";
import { useToasts } from "react-toast-notifications";

// Constants
import colors from "styles/colors";
import typography, { applyTypography } from "styles/typography"

import {
  CopilotDataProvider as CopilotDataProviderV2,
  useCopilotDataContext as useCopilotDataContextV2,
} from "./Data/CopilotDataContextV2";

import {
  FeedbackProvider,
  useFeedbackContext,
} from "./Data/FeedbackDataContext";
import { FilterProvider } from "Providers/FilterProvider";

// Data
import {
  FileManagementProvider,
  useFileManagement,
} from "./UploadModalContext";
import DeleteModal from "new_components/organisms/DeleteModal";
import PresetPromptCard from "new_components/molecules/PresetPromptCard";
import Filters from "./components/Filters";

// Styles
const CopilotContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0px;
  margin-top: 10%;
  margin-bottom: 20px;
`;

const SelectedData = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 0px;
  flex-direction: row;
  width: 100%;
  justify-content: space-between;
`;

const PromptOptions = styled.div`
  display: flex;
  gap: 8px;
  flex-direction: row;
  width: 100%;
  justify-content: flex-start;
  align-items: flex-start;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: min(770px, max(550px, 100%));
  margin: 0 auto;
  padding-top: 0px;

`;

const MessageSpacing = styled.div`
  margin-bottom: 16px;
`;

// Allowed MIME Types
const mimeToFileType = {
  "application/pdf": "pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/msword": "doc",
};

const InputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(770px, max(550px, 100%));
  margin: 0 auto;
  z-index: 1000;
`;

const NewIcon = styled.div`
  height: 36px;
  width: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid ${colors.neutral[200]};
  color: ${colors.neutral[600]};
  cursor: ${(props) => (props.enabled ? `pointer !important` : "not-allowed")};

  &:hover {
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[300]};
  }
`;

const NewIconWithText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 16px;
  height: 36px;
  padding: 0 8px;
  border: 1px solid ${colors.neutral[200]};
  color: ${colors.text.gray};
  cursor: ${(props) =>
    !props.disabled ? `pointer !important` : "not-allowed"};

  &:hover {
    background-color: ${colors.neutral[100]};
    border-color: ${colors.neutral[300]};
  }
`;

const PromptWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  right: 0;
`;

const ReconnectBadge = styled.div`
  position: sticky;
  top: 20px;
  left: 0;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 4px 14px;
  margin-bottom: 18px;
  background: #fff0f1;
  border: 1.5px solid #ffd6d9;
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(255, 0, 56, 0.08);
  color: ${colors.auxiliary.red[500]};
  font-weight: 500;
  font-size: 12px;
`;

const ReconnectButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: ${colors.auxiliary.red[500]};
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 6px 16px;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  box-shadow: 0 1px 4px 0 rgba(215, 38, 61, 0.10);
  transition: background 0.2s;
  &:hover {
    background: ${colors.auxiliary.red[600]};
  }

  ${applyTypography(typography.caption.semibold)}
`;

const DataCardsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: 16px;
  max-width: 100%;
  
  & > div {
    flex: 0 0 calc(50% - 4px);
    
    @media (max-width: 600px) {
      flex: 0 0 100%;
    }
  }
`;

// Add new styled components for rendering cards directly
const ChatDataCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  background-color: ${colors.background.lightBlue};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 150px;
  max-width: 200px;
  flex-shrink: 0;
`;

const CardText = styled.div`
  font-size: 12px;
  color: ${colors.text.gray};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
`;

// CONSIDER MOVING THIS FUNCTION TO A HELPER FILE
const getOutcomeOptions = (outcomeQ) => {
  return outcomeQ?.questions?.map((q) => {
    return {
      label: q.name,
      value: {
        id: q.id,
        title: q.name,
        level: 3,
        type: "outcome",
      },
    };
  });
};

const generateFactorConfig = (dimData, callback, removeCb, removeAllCb) => {
  const factors = dimData?.flatMap(({ factors }) => factors);

  const factorData = {
    label: "Engagement Survey",
    value: "culture",
    selectText: "Select engagement survey factors",
    settings: ["multi"],
    options: factors?.map((factor) => {
      return {
        label: factor?.title,
        value: factor,
        type: "culture",
      };
    }),
    callback: callback,
    removeCb: removeCb, // Add a remove callback
    removeAllCb: removeAllCb,
  };

  return [factorData] || [];
};

// Update the chatScreen function to render cards from raw data
const chatScreen = (
  messagesByRoom,
  roomId,
  isPending,
  scrollRef,
  dataCards,
  chatStarted,
  streamingMsg,
  pendingInitialMessage,
  promptCard,
  pendingQuestion
) => {
  if (!messagesByRoom || !roomId) return null;

  // Grab the messages array for this room ID
  const currentRoomMessages = messagesByRoom[roomId] || [];

  const containerStyles = chatStarted
    ? { overflowY: "visible", paddingTop: "80px" }
    : { overflowY: "scroll", paddingTop: "80px" };

  let activeStream;
  let isStreamingActive;
  Object.keys(streamingMsg).forEach((key) => {
    if (
      streamingMsg[key]?.status === "streaming" &&
      streamingMsg[key]?.message &&
      streamingMsg[key]?.web_chat_id === roomId
    ) {
      activeStream = streamingMsg[key];
      isStreamingActive = true;
    }
  });

  // Extract the card data from the dataCards component
  let cardData = [];

  // If dataCards is a React element, try to extract selectedCards from props
  if (React.isValidElement(dataCards)) {
    const { props } = dataCards;
    // Try to get selectedCards from props
    if (props && props.selectedCards && Array.isArray(props.selectedCards)) {
      cardData = props.selectedCards;
    }
  }
  // If dataCards is raw card data (array of card objects)
  else if (Array.isArray(dataCards)) {
    cardData = dataCards;
  }


  // Only show cards if we have data and this isn't the first message
  const hasCards = cardData.length > 0 && currentRoomMessages.length > 0;

  // Pull the streaming object if it matches pendingId
  return (
    <div style={containerStyles}>
      {/* Render card data if it exists and we have messages */}
      {hasCards && (
        <DataCardsContainer>
          {cardData.map((card, index) => (
            <ChatDataCard key={`${card.id || index}`}>
              <CardText>{card.title}</CardText>
            </ChatDataCard>
          ))}
        </DataCardsContainer>
      )}

      {currentRoomMessages?.length === 0 &&
        pendingInitialMessage &&
        !promptCard && (
          <MessageSpacing
            key="initial"
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <ChatDialogBox message={pendingInitialMessage?.message} />
          </MessageSpacing>
        )}
      {currentRoomMessages?.length === 0 &&
        pendingInitialMessage &&
        promptCard && (
          <MessageSpacing
            key="initial"
            style={{
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            {promptCard}
          </MessageSpacing>
        )}
      {currentRoomMessages.map((msg, index) => {
        // If it is the user prompt and it is the first message, and the promptCard is not null, show the promptCard
        if (index === 0 && promptCard) {
          return (
            <MessageSpacing
              key={msg.id || index}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {promptCard}
            </MessageSpacing>
          );
        }

        // 1) If it's a user prompt:
        if (msg.from === "user" || msg.type === "prompt") {
          return (
            <MessageSpacing
              key={msg.id || index}
              style={{
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <ChatDialogBox message={msg.message} />
            </MessageSpacing>
          );
        }

        // If its the last index and the streaming is active, dont show the last message because its being rendered as a streaming message
        if (index === currentRoomMessages.length - 1 && isStreamingActive) {
          return null;
        }

        // 2) If it's an assistant message (completed from messagesByRoom):
        if (msg.from === "assistant" || msg.type === "response") {
          return (
            <MessageSpacing key={msg.id || index}>
              <ChatResponse
                message={msg.message}
                isStreaming={false}
                isThinking={false}
                scrollRef={scrollRef}
                messageId={msg.id}
              />
            </MessageSpacing>
          );
        }

        return null;
      })}

      {/* Always render the component with a stable key, but control its content via props */}
      {Object.values(streamingMsg)?.length > 0 && (
        <MessageSpacing key="ai-response-persistent">
          <ChatResponse
            message={activeStream?.message || ""}
            isStreaming={isStreamingActive}
            isThinking={!isStreamingActive || isPending}
            scrollRef={scrollRef}
            messageId={activeStream?.id || "streaming"}
          />
        </MessageSpacing>
      )}

      {/* 
        This div is used for auto-scroll. 
        We'll call scrollRef.current.scrollIntoView() to jump here 
      */}
      <div ref={scrollRef} />
    </div>
  );
};

const Copilot = (props) => {
  const fileInputRef = useRef();
  const addToast = useToasts();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const preselectedPulseId = searchParams.get('pulseId');

  const {
    feedback,
    getFeedbackParams,
    pulseSurveys,
    outcomeQ,
    factorLabels,
    setSelectedFactors,
    handleSelectPulse,
    handleRemovePulse,
    factorCardData,
    setSelectedOutcomes,
    outcomeCardData,
    handleRemoveFactor,
    handleRemoveOutcome,
    selectedPulses,
    selectedOutcomes,
    selectedFactors,
    newChatClicked,
    feedbackLength,
    handleRemoveAllFactors,
    handleRemoveAllOutcomes,
    customQuestions,
    selectedCustomQuestions,
    handleSelectCustomQuestion,
    handleRemoveCustomQuestion,
    handleRemoveAllCustomQuestions,
    customQuestionsCardData,
    setSelectedCustomQuestions
  } = useFeedbackContext();

  const {
    initiateChat,
    promptText,
    setPromptText,
    chatStarted,
    chatRooms,
    startNewConversation,
    selectedRoomId,
    handleRoomSelect,
    messagesEndRef,
    handleDeleteRoom: handleDelete,
    messagesByRoom,
    handleSendMessage,
    pendingInitialMessage,
    messagePending,
    selectedParams,
    streamingMsg,
    initialPendingId: pendingId,
    usedPrompt,
    feedbackLength: roomFeedbackLength,
    cleanup,
    // TODO: We need to connect the reconnect functionality to the onKeyPress event, it should only trigger if the user presses the enter key.
    reconnect,
    isWebSocketConnected,
  } = useCopilotDataContextV2();

  const { uploadFile, addedFile, uploadInProgress, clearSelectedFile } =
    useFileManagement();

  const dataSourceConfig = [
    {
      label: "Outcome",
      value: "outcome",
      settings: ["multi"],
      options: getOutcomeOptions(outcomeQ), // Create a function to get the list of outcomes
      callback: setSelectedOutcomes,
      selectText: "Select outcome",
      removeCb: handleRemoveOutcome, // Add a remove callback
      removeAllCb: handleRemoveAllOutcomes,
    },
    ...generateFactorConfig(
      factorLabels,
      setSelectedFactors,
      handleRemoveFactor,
      handleRemoveAllFactors
    )
  ];

  // Add Custom Questions to the dataSourceConfig if available
  if (customQuestions?.length > 0) {
    dataSourceConfig.push({
      label: "Custom Questions",
      value: "custom",
      settings: ["multi"],
      options: customQuestions.map(q => ({
        label: q.title,
        value: {
          ...q,
          id: q?.id?.toString() || q?.id // Ensure id is a string as expected by DataSourceCard
        },
        type: "custom",
      })),
      callback: handleSelectCustomQuestion,
      selectText: "Select custom question",
      removeCb: handleRemoveAllCustomQuestions,
      removeAllCb: handleRemoveAllCustomQuestions,
    });
  }

  if (pulseSurveys?.length > 0) {
    dataSourceConfig.push({
      label: "Pulse Surveys",
      value: "pulse",
      options: pulseSurveys,
      callback: handleSelectPulse,
      removeCb: handleRemovePulse,
    })
  }

  const [showPromptModal, setShowPromptModal] = useState(false);
  const [fileSources, setFileSources] = useState([]);
  const [sourceType, setSourceType] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isInvalidFile, setIsInvalidFile] = useState(false);
  const [fileCardData, setFileCardData] = useState([]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [chatSendEnabled, setChatSendEnabled] = useState(false);
  const [chatInputEnabled, setChatInputEnabled] = useState(false);
  const [promptObject, setPromptObject] = useState(null);
  const [pendingQuestion, setPendingQuestion] = useState(null);
  const [hasHandledPreselectedPulse, setHasHandledPreselectedPulse] = useState(false);
  const [loadTimeout, setLoadTimeout] = useState(false);
  const [pendingMessageAfterReconnect, setPendingMessageAfterReconnect] = useState(null);

  // When a new chat is selected, reset the loadTimeout
  useEffect(() => {
    setLoadTimeout(false);
  }, [selectedRoomId]);

  // The input will be disabled until 1 of the following conditions is met:
  useEffect(() => {
    const pulseSelected = selectedPulses.length > 0;
    const outcomesSelected = selectedOutcomes.length > 0;
    const factorsSelected = selectedFactors.length > 0;
    const customQuestionsSelected = selectedCustomQuestions.length > 0;
    const feedbackProvided = feedbackLength > 0;
    // Check to see if the user has added a data source
    const dataSourcesAdded = fileCardData.length > 0;

    // Enable input if chat started, any pulse selected,
    // or if outcomes selected AND feedback provided,
    // or if factors/custom questions + feedback present,
    // or any file source added
    if (
      chatStarted ||
      pulseSelected ||
      (outcomesSelected && feedbackProvided) ||
      ((factorsSelected || customQuestionsSelected) && feedbackProvided) ||
      dataSourcesAdded
    ) {
      setChatInputEnabled(true);
    } else {
      setChatInputEnabled(false);
    }
  }, [
    chatStarted,
    selectedPulses,
    selectedOutcomes,
    selectedFactors,
    selectedCustomQuestions,
    feedbackLength,
    fileCardData,
  ]);

  const fileUploadEnabled = true;

  // The send button will be disabled until the user has entered a message
  useEffect(() => {
    if (promptText?.length > 0 || promptObject) {
      setChatSendEnabled(true);
    } else {
      setChatSendEnabled(false);
    }
  }, [promptText, promptObject]);

  // Add effect to handle browser navigation
  useEffect(() => {
    // Listen for unload events
    const handleCleanup = () => {
      setPendingMessageAfterReconnect(null);
      cleanup();
    };

    window.addEventListener('beforeunload', handleCleanup);
    
    // Add a one-time popstate listener that triggers on navigation
    const handlePopState = () => {
      setPendingMessageAfterReconnect(null);
      cleanup();
      // Remove listener after first trigger to prevent multiple calls
      window.removeEventListener('popstate', handlePopState);
    };
    
    window.addEventListener('popstate', handlePopState);
    
    // This will run when the component unmounts
    return () => {
      window.removeEventListener('beforeunload', handleCleanup);
      window.removeEventListener('popstate', handlePopState);
      setPendingMessageAfterReconnect(null);
      cleanup();
    };
  }, []);
  
  // Router integration - run this effect once on mount
  useEffect(() => {
    // Create a custom popstate event listener that manually checks URL changes
    const checkNavigation = () => {
      const currentPath = window.location.pathname;
      // Store the current path in sessionStorage
      const previousPath = sessionStorage.getItem('copilotPath');
      
      // If there was a previous path and it's different, we've navigated
      if (previousPath && previousPath !== currentPath) {
        setPendingMessageAfterReconnect(null);
        cleanup();
      }
      
      // Update the stored path
      sessionStorage.setItem('copilotPath', currentPath);
    };
    
    // Run once on component mount
    checkNavigation();
    
    // Set up interval to periodically check for navigation
    const intervalId = setInterval(checkNavigation, 500);
    
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  const updateTimeout = () => {
    setLoadTimeout(false);

    setTimeout(() => {
      setLoadTimeout(true);
    }, 1000);
  }

  // Load timeout is meant to prevent the flicker of the reconnect badge when the page is loaded
  useEffect(() => {
    if (isWebSocketConnected) {
      const timeout = setTimeout(() => {
        setLoadTimeout(true);
      }, 1000);
      return () => clearTimeout(timeout);
    }

  }, [isWebSocketConnected]);

  useEffect(() => {
    // If a file is added, add it to the fileSources array
    if (addedFile) {
      setFileSources([...fileSources, addedFile]);
      // Add the file to the fileCardData array
      setFileCardData(
        fileCardData.concat({
          type: "file",
          id: addedFile.id,
          title: addedFile.name,
          status: "New",
        })
      );
    }
  }, [addedFile]);

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragging(true);
    if (uploadInProgress) {
      setIsInvalidFile(true);
    }
  };

  // Create function to remove file from fileSources array
  const removeFile = (fileId) => {
    const updatedFiles = fileSources.filter((file) => file.id !== fileId);
    setFileSources(updatedFiles);
    clearSelectedFile(fileId);
    setFileCardData(fileCardData.filter((card) => card.id !== fileId));
  };

  const handleDragLeave = () => {
    setIsDragging(false);
    setIsInvalidFile(false); // Reset error state when drag leaves
  };

  const handleBrowseClick = () => {
    if (chatStarted) {
      addToast.addToast("You cannot upload files during an active chat.", {
        appearance: "info",
      });
      return;
    }
    fileInputRef.current.click();
  };

  const dataCards = (
    <DataSourceCard
      dataSourceConfig={dataSourceConfig}
      chatStarted={chatStarted}
      // Add the type: factor to the selectedFactors array
      selectedCards={
        selectedParams?.length > 0
          ? selectedParams
          : [
              ...selectedPulses,
              ...factorCardData,
              ...outcomeCardData,
            ...customQuestionsCardData,
              ...fileCardData,
            ]
      }
      uploadPending={uploadInProgress}
      fileClearCb={removeFile}
      feedbackLength={roomFeedbackLength || feedbackLength}
      handleRemoveAllFactors={handleRemoveAllFactors}
      handleRemoveAllOutcomes={handleRemoveAllOutcomes}
      handleRemoveAllCustomQuestions={handleRemoveAllCustomQuestions}
    />
  );

  const promptCard = usedPrompt ? (
    <PresetPromptCard prompt={usedPrompt?.title} />
  ) : null;

  // startChat
  function createChat() {}

  const chatHeading = (
    <CopilotContainer>
      <AILogo />
      <Text
        typographyStyle={typography.emptyHeader.regular}
        color={colors.text.dark}
      >
        Welcome to Copilot
      </Text>
      <Text
        typographyStyle={typography.body.regular}
        color={colors.text.gray}
        style={{ maxWidth: "400px", textAlign: "center" }}
      >
        Copilot is an AI assistant that helps you understand your culture and
        engagement data.
      </Text>
    </CopilotContainer>
  );

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    uploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);

    if (chatStarted) {
      addToast.addToast("You cannot upload files during an active chat.", {
        appearance: "info",
      });
      return;
    }

    if (uploadInProgress) {
      addToast.addToast("Please wait for the current upload to finish.", {
        appearance: "info",
      });
      return;
    }

    const file = event.dataTransfer.files[0];
    const fileType = file?.type;

    if (mimeToFileType[fileType]) {
      setIsInvalidFile(false); // Reset invalid state
      uploadFile(file); // Handle valid file upload
    } else {
      setIsInvalidFile(true); // Show error state
      // add an error toast
      addToast.addToast("Invalid file type. Please upload a PDF or word doc.", {
        appearance: "error",
      });
    }
  };

  const chatComponent = (
    <ChatContainer chatStarted={chatStarted}>
      {/* {!isWebSocketConnected && chatStarted && loadTimeout && messagesByRoom?.[selectedRoomId]?.length > 0 && (
        <ReconnectBadge>
          <Text typographyStyle={typography.caption.semibold} color={colors.auxiliary.red[500]}>
            Connection lost, please reconnect to continue.
            {pendingMessageAfterReconnect && " Message pending..."}
          </Text>
          <ReconnectButton onClick={reconnect}>
            <Icon
              name={ICON_SET.refresh}
              stroke="#fff"
              fill="none"
              size={18}
              style={{ marginRight: 4 }}
            />
            Reconnect
          </ReconnectButton>
        </ReconnectBadge>
      )} */}
      {chatStarted &&
        chatScreen(
          messagesByRoom,
          selectedRoomId,
          messagePending,
          messagesEndRef,
          dataCards,
          chatStarted,
          streamingMsg,
          pendingInitialMessage,
          promptCard,
          pendingQuestion
        )}
    </ChatContainer>
  );

  const sendMessage = () => {
    if (!chatSendEnabled) {
      return;
    }

    if (chatStarted) {
      // If the user sends a message, and the websocket is not connected, we need to connect it before sending the message.
      if (!isWebSocketConnected) {
        // Store the message to send after reconnection
        setPendingMessageAfterReconnect(promptText);
        setPromptText(""); // Clear the input field
        reconnect();
        return;
      }

      handleSendMessage(promptText);
      // Set the pending question
      setPendingQuestion(promptText);
    } else {
      initiateChat(
        promptObject?.categoryContent?.props?.prompt?.prompt || promptText,
        feedback,
        getFeedbackParams(fileSources, promptObject)
      );
    }
    setPromptObject(null);
  };

  const inputBox = (
    <InputContainer>
      <div style={{ marginBottom: 10 }}>
        {!chatStarted && (
          <DataSource
            selectedSources={[
              ...selectedPulses,
              ...factorCardData,
              ...outcomeCardData,
              ...customQuestionsCardData,
            ]}
            setSourceType={setSourceType}
            dataSourceConfig={dataSourceConfig}
            handleRemoveAllFactors={handleRemoveAllFactors}
            handleRemoveAllOutcomes={handleRemoveAllOutcomes}
          />
        )}
        {!chatStarted && (
          <Filters enableFilters={(selectedFactors.length > 0 || selectedCustomQuestions.length > 0) && selectedPulses.length === 0} />
        )}
      </div>

        <DescriptionInput
        value={promptText}
        onChange={(e) => setPromptText(e.target.value)}
        placeholder="Ask me anything..."
        handleDragOver={handleDragOver}
        handleDragLeave={handleDragLeave}
        sendEnabled={chatSendEnabled}
        inputEnabled={chatInputEnabled}
        handleDrop={handleDrop}
        isDragging={isDragging}
        sourceCards={dataCards}
        presetPrompt={promptObject}
        fileUploadEnabled={fileUploadEnabled}
        clearPrompt={() => {
          setPromptObject(null);
          setPromptText("");
        }}
        isInvalidFile={isInvalidFile}
        chatStarted={chatStarted}
        uploadRef={
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
            accept={Object.keys(mimeToFileType).join(",")}
          />
        }
        handleEnter={() => {
          const pulseSelected = selectedPulses.length > 0;
          const outcomesSelected = selectedOutcomes.length > 0;
          const factorsSelected = selectedFactors.length > 0;
          const customQuestionsSelected = selectedCustomQuestions.length > 0;
          const feedbackProvided = feedbackLength > 0;

          // Only block if chat hasn't started and no valid data source with feedback is present
          if (
            !chatStarted &&
            !(
              (pulseSelected && feedbackProvided) ||
              (outcomesSelected && feedbackProvided) ||
              ((factorsSelected || customQuestionsSelected) && feedbackProvided) ||
              fileCardData.length > 0
            )
          ) {
            addToast.addToast("Your data source must have feedback.", {
              appearance: "error",
            });
            return;
          }
          sendMessage();
        }}
        bottomActionRow={
          <Actions
            onMouseOver={(e) => {
              e.stopPropagation();
            }}
            style={{
              justifyContent: chatStarted ? "flex-end" : "space-between",
            }}
          >
            {!chatStarted && (
              <PromptOptions>
                {fileUploadEnabled && (

                <Tooltip key="uploadTooltip" tooltipText="Upload a file">
                  <NewIcon
                    style={{
                      zIndex: 999,
                      cursor: chatStarted ? "not-allowed" : "pointer",
                    }}
                    enabled={true}
                    onClick={() => handleBrowseClick()}
                  >
                    <Icon
                      style={{
                        cursor: chatStarted
                          ? "not-allowed"
                          : "pointer !important",
                        zIndex: 1,
                      }}
                      name="add"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBrowseClick();
                      }}
                      color={colors.neutral[300]}
                      size={20}
                    />
                  </NewIcon>
                </Tooltip>
                )}
                <NewIconWithText
                  disabled={!chatInputEnabled || chatStarted}
                  onClick={() => {
                    if (!chatInputEnabled || chatStarted) {
                      return;
                    }
                    setShowPromptModal(true);
                  }}
                >
                  <Icon
                    name={ICON_SET.layers}
                    color={colors.neutral[300]}
                    size={20}
                    style={{
                      marginRight: 5,
                      cursor: chatStarted ? "not-allowed" : "pointer !important",
                    }}
                  />
                  Prompt library
                </NewIconWithText>
              </PromptOptions>)
            }
            <Tooltip
              key="promptTooltip"
              tooltipText={
                !chatStarted && !(selectedPulses.length && feedbackLength) && !(selectedFactors.length && feedbackLength) && !(selectedCustomQuestions.length && feedbackLength) && !(selectedOutcomes.length && feedbackLength) && fileCardData.length === 0
                  ? "Your data source must have feedback."
                  : !chatSendEnabled
                  ? "Message cannot be blank."
                  : "You must first add a data source."
              }
              showTip={!chatSendEnabled}
            >
              <NewIcon enabled={chatSendEnabled && (chatStarted || (selectedPulses.length > 0 && feedbackLength > 0) || (selectedFactors.length > 0 && feedbackLength > 0) || (selectedCustomQuestions.length > 0 && feedbackLength > 0) || fileCardData.length > 0)}>
                <Icon
                  name={ICON_SET.send}
                  size={20}
                  style={{
                    cursor: chatSendEnabled ? "pointer" : "not-allowed",
                  }}
                  onClick={() => {
                    if (chatSendEnabled && (chatStarted || (selectedPulses.length > 0 && feedbackLength > 0) || (selectedFactors.length > 0 && feedbackLength > 0) || (selectedCustomQuestions.length > 0 && feedbackLength > 0) || (selectedOutcomes.length > 0 && feedbackLength > 0) || fileCardData.length > 0)) {
                      sendMessage()
                    }
                  }}
                />
              </NewIcon>
            </Tooltip>
          </Actions>
        }
      />
    </InputContainer>
  );

  // Whenever messages change or we send a new message, scroll to the bottom
  React.useEffect(() => {
    if (chatStarted && messagesEndRef?.current) {
      setTimeout(() => {
        messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
      }, 500);
    }
  }, [chatStarted, selectedRoomId]);

  // Add effect to handle preselected pulse
  useEffect(() => {
    if (preselectedPulseId && pulseSurveys?.length > 0 && selectedPulses?.length === 0 && !hasHandledPreselectedPulse) {
      // Find the pulse in the available pulses
      const pulse = pulseSurveys.find(p => p.id === Number(preselectedPulseId));
      if (pulse) {
        // Select the pulse using the handleSelectPulse function
        handleSelectPulse(Number(preselectedPulseId));

        // Update the url to remove the ?pulseId=336 parameter
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);

        // Enable chat input since we have a pulse selected
        setChatInputEnabled(true);

        // Mark that we've handled the preselected pulse
        setHasHandledPreselectedPulse(true);
      }
    }
  }, [preselectedPulseId, pulseSurveys, handleSelectPulse, handleRemoveAllFactors, handleRemoveAllOutcomes, hasHandledPreselectedPulse]);

  // Effect to handle sending pending message after reconnection
  useEffect(() => {
    if (isWebSocketConnected && pendingMessageAfterReconnect && chatStarted) {
      // Connection is restored and we have a pending message
      handleSendMessage(pendingMessageAfterReconnect);
      setPendingQuestion(pendingMessageAfterReconnect);
      setPendingMessageAfterReconnect(null);
      // Ensure prompt text is cleared
      setPromptText("");
    }
  }, [isWebSocketConnected, pendingMessageAfterReconnect, chatStarted, handleSendMessage]);

  return (
    <ChatTemplate
      chatHeading={chatHeading}
      chatComponent={chatComponent}
      chatStarted={chatStarted}
      activeRoom={selectedRoomId}
      fromPulseSurvey={!!preselectedPulseId}
      startNewChat={() => {
        // Clear the cards
        setFileCardData([]);
        setFileSources([]);
        setSelectedFactors([]);
        setSelectedOutcomes([]);
        setSelectedCustomQuestions([]);
        setChatSendEnabled(false);
        setChatInputEnabled(false);
        clearSelectedFile();
        newChatClicked();
        setLoadTimeout(false);
        setPendingMessageAfterReconnect(null);
        startNewConversation();
      }}
      inputBox={inputBox}
      submenuData={{
        sectionOne: (
          <ChatHistory
            setPendingDelete={setPendingDelete}
            chatTopics={chatRooms?.chat_topics}
            activeChatId={selectedRoomId}
            onChatSelect={(roomId) => {
              updateTimeout();
              handleRoomSelect(roomId);
            }}
          />
        ),
        sectionTwo: null,
      }}
      modals={
        <>
          <DeleteModal
            itemName="chat history"
            isOpen={!!pendingDelete}
            onClose={() => setPendingDelete(null)}
            onConfirm={() => {
              // Check if we're deleting the active room
              const isActiveRoom = pendingDelete === selectedRoomId;

              handleDelete(pendingDelete);

              // If we deleted the active room, clear all local state
              if (isActiveRoom) {
                setFileCardData([]);
                setFileSources([]);
                setSelectedFactors([]);
                setSelectedOutcomes([]);
                setSelectedCustomQuestions([]);
                setChatSendEnabled(false);
                setChatInputEnabled(false);
                clearSelectedFile();
                newChatClicked();
                setLoadTimeout(false);
              }

              setPendingDelete(null);
            }}
          />
          <PromptModal
            isOpen={showPromptModal}
            onClose={() => setShowPromptModal(false)}
            onConfirm={(selectedPrompt, selectedPromptObject) => {
              setPromptObject(selectedPromptObject);

              setShowPromptModal(false);
            }}
          />
        </>
      }
    ></ChatTemplate>
  );
};

// PropTypes
Copilot.propTypes = {
  // ...
};

const CopilotPage = () => {
  return (
    <CopilotDataProviderV2>
      <FeedbackProvider>
        <FilterProvider>
          <FileManagementProvider>
            <Copilot />
          </FileManagementProvider>
        </FilterProvider>
      </FeedbackProvider>
    </CopilotDataProviderV2>
  );
};

export default CopilotPage;
